import { useState, useEffect } from 'react';
import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { DatePicker } from '@/components/application/date-picker/date-picker';
import { Button } from '@/components/base/buttons/button';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import type { Container, ContainerStatus, DocumentStatus, RiskLevel } from '@/types/container';
import { useI18n } from '@/hooks/use-i18n';
import { XCircle } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';

interface ContainerEditModalProps {
    container: Container;
    field: string;
    value: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: (value: any) => void;
    isLoading?: boolean;
}

// Helper per convertire una data in CalendarDate in modo sicuro
// Crea CalendarDate direttamente dai componenti invece di usare toCalendarDate
const toCalendarDateSafe = (date: Date | string | null | undefined): CalendarDate | null => {
    if (!date) return null;
    
    let dateObj: Date;
    if (date instanceof Date) {
        dateObj = date;
    } else if (typeof date === 'string') {
        dateObj = new Date(date);
    } else {
        return null;
    }
    
    // Valida che la data sia valida
    if (isNaN(dateObj.getTime())) {
        return null;
    }
    
    try {
        // Crea CalendarDate direttamente dai componenti (più sicuro di toCalendarDate)
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1; // JS months are 0-based, CalendarDate uses 1-based
        const day = dateObj.getDate();
        return new CalendarDate(year, month, day);
    } catch (e) {
        return null;
    }
};

export const ContainerEditModal = ({
    container,
    field,
    value,
    isOpen,
    onClose,
    onSave,
    isLoading = false
}: ContainerEditModalProps) => {
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const { t } = useTranslation(['pages:containers', 'common']);
    const [editValue, setEditValue] = useState<any>(value);

    // Inizializza editValue quando il modale si apre o quando value cambia
    useEffect(() => {
        if (isOpen) {
            if (field === 'value') {
                // Gestione speciale per value (oggetto con amount e currency)
                const currentValue = value && typeof value === 'object' && 'amount' in value && 'currency' in value
                    ? value as { amount: number; currency: string }
                    : container.value || { amount: 0, currency: 'USD' };
                setEditValue(currentValue);
            } else if (field === 'eta') {
                // Gestione speciale per eta: assicurati che sia una Date valida
                if (value instanceof Date && !isNaN(value.getTime())) {
                    setEditValue(value);
                } else if (typeof value === 'string') {
                    const dateObj = new Date(value);
                    if (!isNaN(dateObj.getTime())) {
                        setEditValue(dateObj);
                    } else {
                        setEditValue(container.eta);
                    }
                } else {
                    setEditValue(container.eta);
                }
            } else {
                setEditValue(value);
            }
        }
    }, [isOpen, value, field, container]);

    const handleSave = () => {
        onSave(editValue);
        onClose();
    };

    const getFieldLabel = (): string => {
        switch (field) {
            case 'progress':
                return t('list.table.headers.progress');
            case 'eta':
                return 'ETA';
            case 'status':
                return t('list.table.headers.status');
            case 'physicalStatus':
                return 'Physical Status';
            case 'shippingCompany':
                return 'Shipping Company';
            case 'documentStatus':
                return t('list.table.headers.documentStatus');
            case 'riskLevel':
                return t('list.table.headers.riskLevel');
            case 'value':
                return t('list.table.headers.value');
            case 'actualDepartureDate':
                return 'Actual Departure Date';
            case 'destinationPort':
                return 'Destination Port';
            default:
                return field;
        }
    };

    const renderEditField = () => {
        switch (field) {
            case 'progress':
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Input
                            type="number"
                            value={String(editValue ?? 0)}
                            onChange={(val) => {
                                const num = parseInt(val) || 0;
                                setEditValue(Math.max(0, Math.min(100, num)));
                            }}
                            size="md"
                            className="w-full"
                        />
                    </div>
                );
            
            case 'eta':
                // Assicurati che editValue sia sempre una Date valida o null
                let etaDate: Date | null = null;
                if (editValue instanceof Date && !isNaN(editValue.getTime())) {
                    etaDate = editValue;
                } else if (editValue && typeof editValue === 'string') {
                    const parsed = new Date(editValue);
                    if (!isNaN(parsed.getTime())) {
                        etaDate = parsed;
                    }
                }
                
                const currentDate = toCalendarDateSafe(etaDate);
                
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <DatePicker
                            value={currentDate}
                            onChange={(date) => {
                                if (date) {
                                    const newDate = date.toDate(getLocalTimeZone());
                                    setEditValue(newDate);
                                } else {
                                    setEditValue(null);
                                }
                            }}
                            onApply={() => {
                                // Il valore è già stato impostato in onChange
                            }}
                        />
                    </div>
                );
            
            case 'status':
                const statusOptions = [
                    { id: 'In Transit', label: translateContainerStatus('In Transit') },
                    { id: 'Delivered', label: translateContainerStatus('Delivered') },
                    { id: 'Delayed', label: translateContainerStatus('Delayed') },
                    { id: 'At Port', label: translateContainerStatus('At Port') }
                ];
                
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Select
                            selectedKey={editValue as ContainerStatus}
                            onSelectionChange={(key) => {
                                setEditValue(key);
                            }}
                            size="md"
                        >
                            {statusOptions.map((option) => (
                                <Select.Item key={option.id} id={option.id}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select>
                    </div>
                );
            
            case 'shippingCompany':
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Input
                            value={String(editValue ?? '')}
                            onChange={setEditValue}
                            size="md"
                            className="w-full"
                            placeholder="MSC, Maersk, etc."
                        />
                    </div>
                );
            
            case 'documentStatus':
                const documentStatusOptions = [
                    { id: 'Completo', label: translateDocumentStatus('Completo') },
                    { id: 'Pending', label: translateDocumentStatus('Pending') },
                    { id: 'Error', label: translateDocumentStatus('Error') }
                ];
                
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Select
                            selectedKey={editValue as DocumentStatus}
                            onSelectionChange={(key) => {
                                setEditValue(key);
                            }}
                            size="md"
                        >
                            {documentStatusOptions.map((option) => (
                                <Select.Item key={option.id} id={option.id}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select>
                    </div>
                );
            
            case 'riskLevel':
                const riskLevelOptions = [
                    { id: 'Low', label: translateRiskLevel('Low') },
                    { id: 'Medium', label: translateRiskLevel('Medium') },
                    { id: 'High', label: translateRiskLevel('High') },
                    { id: 'Critical', label: translateRiskLevel('Critical') }
                ];

                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Select
                            selectedKey={editValue as RiskLevel}
                            onSelectionChange={(key) => {
                                setEditValue(key);
                            }}
                            size="md"
                        >
                            {riskLevelOptions.map((option) => (
                                <Select.Item key={option.id} id={option.id}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select>
                    </div>
                );

            case 'physicalStatus':
                const physicalStatusOptions = [
                    { id: 'PENDING', label: 'Pending' },
                    { id: 'GATE_OUT_EMPTY', label: 'Gate Out (Empty)' },
                    { id: 'LOADING', label: 'Loading' },
                    { id: 'GATE_IN', label: 'Gate In' },
                    { id: 'LOAD_ON_VESSEL', label: 'Loaded on Vessel' },
                    { id: 'VESSEL_DEPARTED', label: 'Vessel Departed' },
                    { id: 'IN_TRANSIT', label: 'In Transit' },
                    { id: 'VESSEL_ARRIVED', label: 'Vessel Arrived' },
                    { id: 'DISCHARGED', label: 'Discharged' },
                    { id: 'GATE_OUT_DELIVERY', label: 'Gate Out (Delivery)' },
                    { id: 'DELIVERED', label: 'Delivered' },
                    { id: 'ON_HOLD', label: 'On Hold' },
                    { id: 'CANCELLED', label: 'Cancelled' }
                ];

                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Select
                            selectedKey={editValue as string}
                            onSelectionChange={(key) => {
                                setEditValue(key);
                            }}
                            size="md"
                        >
                            {physicalStatusOptions.map((option) => (
                                <Select.Item key={option.id} id={option.id}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select>
                    </div>
                );

            case 'actualDepartureDate':
                let actualTdDate: Date | null = null;
                if (editValue instanceof Date && !isNaN(editValue.getTime())) {
                    actualTdDate = editValue;
                } else if (editValue && typeof editValue === 'string') {
                    const parsed = new Date(editValue);
                    if (!isNaN(parsed.getTime())) {
                        actualTdDate = parsed;
                    }
                }

                const actualTdCalendarDate = toCalendarDateSafe(actualTdDate);

                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <DatePicker
                            value={actualTdCalendarDate}
                            onChange={(date) => {
                                if (date) {
                                    const newDate = date.toDate(getLocalTimeZone());
                                    setEditValue(newDate);
                                } else {
                                    setEditValue(null);
                                }
                            }}
                            onApply={() => {}}
                        />
                    </div>
                );

            case 'destinationPort':
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Input
                            value={String(editValue ?? '')}
                            onChange={setEditValue}
                            size="md"
                            className="w-full"
                            placeholder="Port name"
                        />
                        <p className="text-xs text-tertiary mt-1">
                            Note: Port lookup coming soon. Enter port name for now.
                        </p>
                    </div>
                );

            case 'value':
                const currentValue = editValue && typeof editValue === 'object' && 'amount' in editValue && 'currency' in editValue
                    ? editValue as { amount: number; currency: string }
                    : { amount: 0, currency: 'USD' };
                
                const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'JPY'];
                
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <div className="flex items-center gap-2">
                            <Select
                                selectedKey={currentValue.currency}
                                onSelectionChange={(key) => {
                                    const newCurrency = key as string;
                                    const newValue = { amount: currentValue.amount, currency: newCurrency };
                                    setEditValue(newValue);
                                }}
                                size="md"
                                className="w-32"
                            >
                                {currencies.map((curr) => (
                                    <Select.Item key={curr} id={curr}>
                                        {curr}
                                    </Select.Item>
                                ))}
                            </Select>
                            <Input
                                type="number"
                                value={String(currentValue.amount || 0)}
                                onChange={(val) => {
                                    const num = parseFloat(val) || 0;
                                    const newValue = { amount: Math.max(0, num), currency: currentValue.currency };
                                    setEditValue(newValue);
                                }}
                                size="md"
                                className="flex-1"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                );
            
            default:
                return (
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {getFieldLabel()}
                        </label>
                        <Input
                            value={String(editValue ?? '')}
                            onChange={setEditValue}
                            size="md"
                            className="w-full"
                        />
                    </div>
                );
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={onClose}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-primary">
                                Modifica {getFieldLabel()}
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-tertiary hover:text-primary transition-colors"
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>
                        <div className="space-y-4 mb-6">
                            <div className="text-sm text-tertiary mb-2">
                                Container: <span className="font-medium text-primary">{container.containerNumber}</span>
                            </div>
                            {renderEditField()}
                        </div>
                        <div className="flex items-center justify-end gap-3">
                            <Button
                                size="md"
                                color="secondary"
                                onClick={onClose}
                                isDisabled={isLoading}
                            >
                                {t('buttons.cancel', { ns: 'common' })}
                            </Button>
                            <Button
                                size="md"
                                color="primary"
                                onClick={handleSave}
                                isDisabled={isLoading}
                                isLoading={isLoading}
                            >
                                {t('buttons.save', { ns: 'common' }) || 'Salva'}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};

